/**
 * React Query hooks for BYOM (Bring Your Own Model)
 * 
 * Provides data fetching and mutations for tenant-owned LLM providers.
 * All queries are scoped to the current tenant.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '@/lib/api-client';
import { useTenant } from '@/contexts/TenantContext';
import type { 
  TenantProvider, 
  TenantProviderCreate, 
  TenantProviderUpdate,
  TenantProviderListResponse,
  ProviderValidationResult,
} from '../types';

const TENANT_PROVIDERS_KEY = 'tenant-providers';

/**
 * Fetch all tenant-owned providers for the current tenant
 */
export function useTenantProviders() {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [TENANT_PROVIDERS_KEY, tenantId],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.get<TenantProviderListResponse>(
        '/api/v1/llm/tenant-providers',
      );
      return data.items;
    },
    enabled: !!tenantId,
  });
}

/**
 * Fetch single tenant provider by ID
 */
export function useTenantProvider(id: string) {
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useQuery({
    queryKey: [TENANT_PROVIDERS_KEY, tenantId, id],
    queryFn: async () => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.get<TenantProvider>(
        `/api/v1/llm/tenant-providers/${id}`,
      );
      return data;
    },
    enabled: !!id && !!tenantId,
  });
}

/**
 * Create new tenant provider
 */
export function useCreateTenantProvider() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (providerData: TenantProviderCreate) => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.post<TenantProvider>(
        '/api/v1/llm/tenant-providers',
        providerData,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_PROVIDERS_KEY] });
    },
  });
}

/**
 * Update existing tenant provider
 */
export function useUpdateTenantProvider(id: string) {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (providerData: TenantProviderUpdate) => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.put<TenantProvider>(
        `/api/v1/llm/tenant-providers/${id}`,
        providerData,
      );
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_PROVIDERS_KEY] });
    },
  });
}

/**
 * Delete tenant provider
 */
export function useDeleteTenantProvider() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      await apiClient.delete(`/api/v1/llm/tenant-providers/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [TENANT_PROVIDERS_KEY] });
    },
  });
}

/**
 * Validate tenant provider credentials
 */
export function useValidateTenantProvider() {
  const queryClient = useQueryClient();
  const { currentTenant } = useTenant();
  const tenantId = currentTenant?.id;

  return useMutation({
    mutationFn: async (id: string) => {
      if (!tenantId) {
        throw new Error('No tenant selected');
      }
      const { data } = await apiClient.post<ProviderValidationResult>(
        `/api/v1/llm/tenant-providers/${id}/validate`,
        {},
      );
      return data;
    },
    onSuccess: () => {
      // Refresh the list to show updated validation status
      queryClient.invalidateQueries({ queryKey: [TENANT_PROVIDERS_KEY] });
    },
  });
}
